import { useEffect, useRef, useState } from 'react';

import { migrateLegacySavedFilters } from './savedFiltersStorageKey';
import { useSavedFilterOwner } from './useSavedFilterOwner';

/**
 * Saved Filters written before the key carried an owner belong to whoever signs in first after the
 * upgrade: one pass moves every ownerless key under them and deletes the original, so a person who
 * already had presets keeps them and the transfer can happen at most once.
 *
 * Returns whether that pass has settled, which is what holds routes back until it has.
 *
 * The pass is latched behind a ref rather than left to an effect's dependencies: StrictMode
 * double-invokes effects, and a migration is not idempotent the way the effects it sits beside are.
 * It also stays out of the render phase — this module already performs render-phase ref writes, and
 * a storage write must not join them.
 *
 * An unresolved owner writes nothing and still settles. Nothing here may strand the app behind an
 * identity the gateway chain has already accepted, so a corrupt claim costs at most the migration:
 * an owner that resolves only after routes have rendered still migrates, but a Saved Filters view
 * that mounted first keeps its already-cached store until the next page load.
 */
export const useLegacySavedFiltersMigration = (): boolean => {
  const owner = useSavedFilterOwner();
  const hasMigratedRef = useRef(false);
  const [hasSettled, setHasSettled] = useState(false);

  useEffect(() => {
    if (owner && !hasMigratedRef.current) {
      hasMigratedRef.current = true;
      migrateLegacySavedFilters(owner);
    }

    setHasSettled(true);
  }, [owner]);

  return hasSettled;
};
